import { Ng2StateDeclaration, StatesModule, Transition } from '@uirouter/angular';
import { FiStateNames } from '../../shared/fi-state-proxy/fi-state-names';
import { FiAbstractStateComponent } from '../../shared/fi-state-proxy/fi-abstract-state.component';
import { FiStateService } from '../../shared/fi-state-proxy/fi-state.proxy.service';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { StateAccessCheckService } from '../../shared/fi-shared-services/state-access-check.service';
import { AccessRightType, ApplicationSettingsType } from '../../shared/types';
import {
    IOnboardingReportContainer,
    IOnboardingReportWidget,
    IOnboardingReportWidgetFilter,
} from './fi-onboarding-report';
import { FiOnboardingReportDataService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report-data.service';
import { FiOnboardingReportHelperService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report-helper.service';
import {
    FiOnboardingReportType,
    FiOnboardingReportWidgetType,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report-enums';
import { FiOnboardingReportParticipantDetailsComponent } from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report-participant-details/fi-onboarding-report-participant-details.component';
import { FiOnboardingReportService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report.service';
import { FiOnboardingReportDashboardComponent } from './fi-onboarding-report-dashboard/fi-onboarding-report-dashboard.component';
import { FiOnboardingReportWidgetDetailsComponent } from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report-details/fi-onboarding-report-details.component';

const OnboardingReportState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Report._name,
    url: '/Report',
    abstract: true,
    component: FiAbstractStateComponent,
    onExit: (tr: Transition, state: Ng2StateDeclaration) => {
        const injector = tr.injector();
        const fiOnboardingReportDataService: FiOnboardingReportDataService =
            injector.get(FiOnboardingReportDataService);
        fiOnboardingReportDataService.resetReportContainer();
    },
};

const reportContainerResolveFn = [
    FiOnboardingReportDataService,
    (fiOnboardingReportDataService: FiOnboardingReportDataService) =>
        fiOnboardingReportDataService.getReportContainer(),
];

const OnboardingReportDashboardState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Report.Dashboard,
    url: '/Dashboard',
    params: {},
    component: FiOnboardingReportDashboardComponent,
    resolve: {
        reportContainer: reportContainerResolveFn,
    },
};

const widgetResolveFn = [
    'reportContainer',
    FiStateService,
    Transition,
    (reportContainer: IOnboardingReportContainer, fiStateService: FiStateService, tr: Transition) => {
        const widgetId = coerceNumberProperty(fiStateService.getParams(tr).id);
        return reportContainer.Widgets.find((widget) => {
            // workaround for opening participant details from personal notifications
            if (widgetId === 0) {
                return widget.ReportType === FiOnboardingReportType.Total;
            }
            return widget.Id === widgetId;
        });
    },
];

const availableItemsResolveFn = [
    'reportContainer',
    (reportContainer: IOnboardingReportContainer) => reportContainer.AvailableItems,
];

const isFilterExpandedResolveFn = [
    FiStateService,
    Transition,
    (fiStateService: FiStateService, tr: Transition) => {
        return fiStateService.getParams(tr).isFilterExpanded;
    },
];

const currentFilterResolveFn = [
    'widget',
    FiOnboardingReportHelperService,
    FiStateService,
    Transition,
    (
        widget: IOnboardingReportWidget,
        fiOnboardingReportHelperService: FiOnboardingReportHelperService,
        fiStateService: FiStateService,
        tr: Transition,
    ) => {
        const currentFilter = fiOnboardingReportHelperService.getReportWidgetFilterFromState(
            widget,{
                stateParams: fiStateService.getParams(tr)
            }
        );
        return currentFilter;
    },
];

const OnboardingReportWidgetState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Report.Widget._name,
    url: '/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiOnboardingReportWidgetDetailsComponent,
        },
    },
    params: {
        isFilterExpanded: false,
    },
    resolve: {
        reportContainer: reportContainerResolveFn,
        widget: widgetResolveFn,
        availableItems: availableItemsResolveFn,
        isFilterExpanded: isFilterExpandedResolveFn,
        reportWidgetType: [
            'widget',
            FiOnboardingReportHelperService,
            (widget: IOnboardingReportWidget, fiOnboardingReportHelperService: FiOnboardingReportHelperService) =>
                fiOnboardingReportHelperService.getReportWidgetTypeForDetails(widget),
        ],
        reportsCount: [
            FiOnboardingReportService,
            (fiOnboardingReportService: FiOnboardingReportService) => fiOnboardingReportService.getReportCount(),
        ],
    },
};

const OnboardingReportWidgetLocationsState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Report.Widget.Locations._name,
    url: '/Onboarding/{onboardingId:[0-9]+}',
    views: {
        '@Module': {
            component: FiOnboardingReportWidgetDetailsComponent,
        },
    },
    params: {
        isFilterExpanded: true,
    },
    resolve: {
        reportContainer: reportContainerResolveFn,
        widget: widgetResolveFn,
        availableItems: availableItemsResolveFn,
        isFilterExpanded: isFilterExpandedResolveFn,
        reportWidgetType: [() => FiOnboardingReportWidgetType.OnboardingLocations],
    },
};

const OnboardingReportWidgetParticipantsState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Report.Widget.Locations.Participants._name,
    url: '/Location/{businessUnitId:[0-9]+}',
    views: {
        '@Module': {
            component: FiOnboardingReportWidgetDetailsComponent,
        },
    },
    params: {
        isFilterExpanded: true,
    },
    resolve: {
        reportContainer: reportContainerResolveFn,
        widget: widgetResolveFn,
        availableItems: availableItemsResolveFn,
        isFilterExpanded: isFilterExpandedResolveFn,
        reportWidgetType: [() => FiOnboardingReportWidgetType.OnboardingParticipants],
    },
};

const highlightedStepIdResolveFn = [
    FiStateService,
    Transition,
    (fiStateService: FiStateService, tr: Transition) => {
        return fiStateService.getParams(tr).highlightedStepId;
    },
];

const OnboardingReportWidgetParticipantDetailsState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Report.Widget.ParticipantDetails._name,
    url: '/Details/{onboardingId:[0-9]+}/{userId:[0-9]+}',
    views: {
        '@Module': {
            component: FiOnboardingReportParticipantDetailsComponent,
        },
    },
    params: {
        isFilterExpanded: true,
        highlightedStepId: null,
    },
    resolve: {
        currentFilter: currentFilterResolveFn,
        completions: [
            'currentFilter',
            FiOnboardingReportService,
            (currentFilter: IOnboardingReportWidgetFilter, fiOnboardingReportService: FiOnboardingReportService) => {
                return fiOnboardingReportService.getOnboardingCompletions(currentFilter);
            },
        ],
        highlightedStepId: highlightedStepIdResolveFn,
    },
};

export const OnboardingReportStates: StatesModule = {
    states: [
        OnboardingReportState,
        OnboardingReportDashboardState,
        OnboardingReportWidgetState,
        OnboardingReportWidgetLocationsState,
        OnboardingReportWidgetParticipantsState,
        OnboardingReportWidgetParticipantDetailsState,
    ],
};

export function registerOnboardingReportStateRules(stateAccessCheckService: StateAccessCheckService) {
    stateAccessCheckService.registerStateRules([FiStateNames.Onboarding.Report._name], {
        SettingType: [ApplicationSettingsType.OnboardingReports],
        AccessType: AccessRightType.Read,
    });
    stateAccessCheckService.registerStateRules([FiStateNames.Onboarding.Report.Dashboard], {
        SettingType: [ApplicationSettingsType.OnboardingReports],
        AccessType: AccessRightType.Read,
    });
}
